import { usePrintShortcut } from './print/usePrintShortcut';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { RSVP_COLORS } from '../lib/rsvpPresentation';
import { useTeamRoleLabel } from '../lib/roles';
import { formatPhotoStamp } from '../lib/photoStamp';
import { huntDetailSearchFor } from '../lib/huntDetailLink';
import type { DocNode, DocumentsOperations, GroupPhoto, Hunt, MapObject, Member, MemberRoleId, Team } from '../lib/types';
import { DocumentsTab } from './DocumentsTab';
import { InviteByEmailOrPhoneModal } from './InviteByEmailOrPhoneModal';
import { Icon, type IconName } from './Icon';
import { MediaLightbox } from './MediaLightbox';
import { Avatar, PageHeader } from './atoms';
import { EstateTag } from './EstateTag';
import { DraftsRail, PendingInvitesRail } from './HuntRails';
import { HuntListRow } from './HuntsPage';
import { TeamMapView } from './TeamMapView';
import { PinFolderTree, type PinCollectionOps } from './PinFolderTree';
import { TeamHeatmapTab, type HeatmapEvent } from './TeamHeatmapTab';
import { useTeamMapPresentation } from './teamMapPresentation';
import { SurfaceIconButton } from './SurfaceIconButton';
import { TeamMapPrintView } from './TeamMapPrintView';
import { groundCenterForPrint } from '../lib/mapPrintRegion';
import type { PrintProbeLookup } from '../lib/printProbe';
import { isTrailCameraPin } from '../lib/mapPinTypes';
import { teamNameMatches } from '../lib/teamNameMatch';

/**
 * A camera shared with the team is stored under its own type rather than as a
 * `camera` with a flag, so counting cameras has to accept both ids — matching
 * on `camera` alone reports none at all for a ground whose cameras are all
 * shared ones.
 */
const CAMERA_PIN_TYPES: ReadonlySet<MapObject['type']> = new Set(['camera', 'camera_shared']);

function cameraCount(objects: MapObject[]): number {
  return objects.filter((o) => CAMERA_PIN_TYPES.has(o.type)).length;
}

/**
 * The team's map pins: every pin document on the ground, whatever each one
 * marks.
 *
 * These counts used to come from a per-type tally read at its `pin` bucket, and
 * that bucket is empty on any real ground: a pin's stored type is the specific
 * thing it marks (`stand`, `tower`, `moose`, `parking`), and the bare `pin` id
 * is only the fallback a missing or unrecognized type normalizes to. So a team
 * with a map full of pins was shown "Map pins 0".
 */
function pinCount(objects: MapObject[]): number {
  // The viewer's own private pins are drawn on this map but are not the team's:
  // counting them would show each member a different total for the same team,
  // and a hunter who imported 500 waypoints would see the team credited with
  // them.
  //
  // Trail cameras are drawn on this map too and are not pins either. They come
  // from the camera service, nothing writes them into the pin collection, and
  // the map editor is never handed them — so the editor's "Map pins" badge
  // counts the pin documents alone. Counting cameras here is what made one
  // ground read "Map pins 3" in the editor and "8 Pins" on this tab, with those
  // same cameras counted a second time by the Cameras chip beside it.
  return objects.filter((o) => !o.isPrivate && !isTrailCameraPin(o)).length;
}

// ─── Inline sample data (previously from @/lib/data) ──────────────────────

const INITIAL_MEMBERS: Member[] = [
  { id: "m-erik",  name: "Erik Lindqvist", roleId: "admin", avatar: "EL", photo: "user-1.png",  you: true, owner: true },
  { id: "m-anna",  name: "Anna Sjoberg",   roleId: "admin", avatar: "AS", photo: "user-2.png"  },
  { id: "m-nils",  name: "Nils Berg",      roleId: "edit",  avatar: "NB", photo: "user-3.png"  },
  { id: "m-maja",  name: "Maja Holm",      roleId: "edit",  avatar: "MH", photo: "user-4.png"  },
  { id: "m-johan", name: "Johan Ek",       roleId: "view",  avatar: "JE", photo: "user-5.png"  },
  { id: "m-lina",  name: "Lina Persson",   roleId: "view",  avatar: "LP", photo: "user-6.png"  },
  { id: "m-oskar", name: "Oskar Dahl",     roleId: "view",  avatar: "OD", photo: "user-7.png"  },
  { id: "m-petra", name: "Petra Lundgren", roleId: "view",  avatar: "PL"                       },
];

const MEMBER_ROLE_IDS: MemberRoleId[] = ["view", "edit", "admin"];

/** Resolve a role's user-facing label/short-label/description via i18next. Kept
    out of module scope so the copy stays localizable (CLUB-277). */
function useMemberRoles(): { id: MemberRoleId; label: string; short: string; desc: string }[] {
  const { t } = useTranslation('teams');
  return MEMBER_ROLE_IDS.map((id) => ({
    id,
    label: t(`detail.roles.${id}.label`),
    short: t(`detail.roles.${id}.short`),
    desc: t(`detail.roles.${id}.desc`),
  }));
}

const AVATAR_PALETTE = ['#20502b', '#8a6d3a', '#6b4423', '#3a5a3f', '#a03c16', '#4a5a3a']

function avatarColor(id: string): string {
  const hash = Array.from(id).reduce((s, c) => s + c.charCodeAt(0), 0)
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
}

const TABS = [
  { id: "overview" },
  { id: "hunts" },
  { id: "map" },
  { id: "heatmap" },
  { id: "statistics" },
  { id: "members" },
  { id: "documents" },
  { id: "pictures" },
  { id: "settings" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export interface TeamDetailPageProps {
  team: Team
  /**
   * A fresh Firebase ID token, forwarded to the map print surface. The
   * server-rendered PDF print needs it because the print session is exchanged
   * from it server-side (premium map data is entitled per signed-in user).
   * Server PDF is the only print path now (ratified product call,
   * 2026-09-01), so without it the Print entry points are hidden rather than
   * opening a surface that has nothing to offer — see MapTab's gate.
   */
  getAuthToken?: () => Promise<string | null>
  /**
   * The print server's probe for a point, forwarded to the print surface so it
   * can draw the sheet in the CRS the server names. Reaching the Estate API is
   * the container's job; without it the print frame is simply not drawn.
   */
  getPrintProbe?: PrintProbeLookup
  teams: Team[]
  hunts: Hunt[]
  onBack: () => void
  onNavigate: (path: string) => void
  tab?: string
  members?: Member[]
  photos?: GroupPhoto[]
  /** True when the server returned only the most recent page of the team's
      camera media. The gallery then says so instead of presenting its count as
      the team's whole collection. */
  photosTruncated?: boolean
  docTree?: Record<string, DocNode>
  ops?: DocumentsOperations
  /** Delete the whole team (Owner-only), after the owner has typed its name. The
      container navigates to the Teams list on success and rejects on failure;
      the confirmation stays open and shows the error, so the owner can retry. */
  onDeleteTeam?: () => void | Promise<void>
  /** Leave the team — every role except the Owner, who has to transfer ownership
      first. The container wires this to the backend and navigates to the Teams
      list on success. */
  onLeaveTeam?: () => void
  /** CLUB-174: persist a member's new role (Owner/Administrator only). The
      container wires this to the backend; rejecting reverts the optimistic UI. */
  onChangeMemberRole?: (memberId: string, roleId: MemberRoleId) => Promise<void>
  /** Remove a member from the team. The UI removes optimistically and restores
      the member if this rejects; the backend enforces who may remove whom. */
  onRemoveMember?: (memberId: string) => Promise<void>
  /** CLUB-198: send a member invite (email or SMS). Returns { ok: false, error }
      to show an inline message in the modal; { ok: true } closes it. */
  onInviteMember?: (input: { email: string; phone: string; country: string }) => Promise<{ ok: boolean; error?: string }>
  /** CLUB-500: generate/regenerate the team join code (Owner/Administrator only).
      The container wires this to the backend; the new code re-reads from my-groups. */
  onGenerateJoinCode?: () => Promise<void>
  /** CLUB-500: delete the team join code (Owner/Administrator only), revoking
      joining until a new code is generated. */
  onDeleteJoinCode?: () => Promise<void>
  /** Persist a new team name. Owner-only — the server enforces it and the card
      is hidden for every other role, matching the mobile management menu where
      "rename team" is offered to the owner and not to an admin. Resolves
      whether or not the write succeeded: the container owns error messaging,
      and this component learns the outcome from the next `team` prop. */
  onRenameTeam?: (name: string) => Promise<void>
  /** Upload and persist a new team picture (Owner/Administrator). Takes the
      picked File — resizing, upload and persistence all belong to the
      container. */
  onChangeTeamPhoto?: (file: File) => Promise<void>
  /** Clear the team picture (Owner/Administrator). */
  onRemoveTeamPhoto?: () => Promise<void>
  /**
   * CLUB-676: what the Map tab's pin collection may do. Omitted → the folder
   * list is read-only, which is what production serves until the write path
   * lands. See PinFolderTree for why each write is opt-in.
   */
  pinCollection?: PinCollectionOps
  /** Historical harvest/observation reports rendered as a density layer on the
      Heatmap tab. The tab only appears when the container supplies these — a
      host without report history simply doesn't offer it. */
  heatmapEvents?: HeatmapEvent[]
  /** The team game-report statistics surface (period picker + totals + by-species
      breakdown), filled by the container with the data-connected component. The
      Statistics tab only appears when this is supplied — a host that does not wire
      it up (e.g. the prototype without mock stats) simply does not offer the tab,
      the same way the Heatmap tab is gated on its events. */
  statisticsSection?: ReactNode
}

export function TeamDetailPage({ team, getAuthToken, getPrintProbe, teams, hunts, onBack, onNavigate, tab: tabProp, members: membersProp, photos: photosProp, photosTruncated, docTree, ops, onDeleteTeam, onLeaveTeam, onChangeMemberRole, onRemoveMember, onInviteMember, onGenerateJoinCode, onDeleteJoinCode, onRenameTeam, onChangeTeamPhoto, onRemoveTeamPhoto, pinCollection, heatmapEvents, statisticsSection }: TeamDetailPageProps) {
  const { t } = useTranslation('teams');
  const tabParam = ((tabProp || "overview").split("&")[0]) as TabId;
  // The team's own settings (picture, name, delete) are Owner / Administrator-only.
  const canManageSettings = team.role === "owner" || team.role === "admin";
  // Every role except the Owner may leave — the Owner would strand the team
  // without one, so they transfer ownership first. The role is re-checked here
  // rather than trusted from the prop alone so that a container wiring the
  // callback for an Owner by mistake still cannot offer an action the backend
  // would refuse.
  const canLeave = onLeaveTeam != null && team.role !== "owner";
  // CLUB-500: the join code is Owner/Administrator-only — members and view-only
  // users don't see it and can't generate/delete it.
  const canManageJoinCode = team.role === "owner" || team.role === "admin";
  const visibleTabs = TABS.filter(
    (tabDef) =>
      // Settings is where leaving lives too, so a plain member gets the tab
      // with only its danger zone; a role with nothing to do there gets none.
      (tabDef.id !== "settings" || canManageSettings || canLeave) &&
      // The Heatmap tab needs report history to draw from; a container that
      // supplies none gets no tab rather than an empty map.
      (tabDef.id !== "heatmap" || heatmapEvents != null) &&
      // The Statistics tab is a filled slot; a host that doesn't wire the
      // data-connected section gets no tab rather than an empty surface.
      (tabDef.id !== "statistics" || statisticsSection != null),
  );
  // A deep link to a tab this role cannot see falls back to Overview.
  const tab = visibleTabs.find((tabDef) => tabDef.id === tabParam) ? tabParam : "overview";
  const joinCode = team.joinCode;
  // The team document is the source of truth for both. A successful write
  // invalidates the my-groups query, the container re-renders with the new
  // values, and these follow. Holding them in local state is what let the
  // Settings tab look like it had saved when it had written nothing at all.
  const teamName = team.name;
  const teamPhoto = team.photo ?? null;
  const [activeTab, setActiveTabState] = useState<TabId>(tab);
  useEffect(() => {
    try {
      window.localStorage.setItem('wehunt:lastTeamId', team.id);
    } catch {}
  }, [team.id]);

  const setTab = (id: TabId) => {
    setActiveTabState(id);
    if (id === "overview") onNavigate(`/teams/${team.id}`);
    else onNavigate(`/teams/${team.id}?tab=${id}`);
  };

  const openMapEditor = () => {
    onNavigate(`/teams/${team.id}/map`);
  };

  const currentTab = activeTab;

  // Built from parts rather than one template literal so the units can be
  // translated and pluralized. `area` is dropped when empty: the region is
  // optional on a group, and an absent one used to leave a dangling "· " at the
  // head of the subtitle.
  const headerSubtitle = [
    team.area,
    t('detail.header.hectares', { count: team.hectares }),
    t('detail.header.members', { count: team.members }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div>
      <PageHeader
        title={teamName}
        titleBadge={team.isEstate ? <EstateTag /> : undefined}
        subtitle={headerSubtitle}
      />

      {/* Breadcrumb above tabs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: COLORS.outline,
          marginTop: -16,
          marginBottom: 16,
        }}
      >
        <button onClick={() => onNavigate('/teams')} style={{ color: COLORS.outline, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
          {t('detail.breadcrumbTeams')}
        </button>
        <Icon name="chevron-right" size={12} />
        <span style={{ color: COLORS.onSurface, fontWeight: 600 }}>{teamName}</span>
      </div>

      <div className="wh-tabs-bar">
        {visibleTabs.map((tabDef) => {
          const active = currentTab === tabDef.id;
          const count =
            tabDef.id === "members" ? team.members :
            tabDef.id === "documents" ? Object.values(docTree ?? {}).filter(n => n.type === "file").length :
            tabDef.id === "pictures" ? (photosProp ?? []).length :
            null;
          return (
            <button
              key={tabDef.id}
              onClick={() => setTab(tabDef.id)}
              style={{
                border: "none",
                background: "none",
                padding: "10px 14px",
                fontSize: 14,
                fontWeight: 600,
                color: active ? COLORS.primary : COLORS.outline,
                borderBottom: `2px solid ${active ? COLORS.primary : "transparent"}`,
                marginBottom: -1,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {t(`detail.tabs.${tabDef.id}`)}
              {count != null && <span style={{ opacity: 0.6, fontWeight: 500 }}> ({count})</span>}
            </button>
          );
        })}
      </div>

      {currentTab === "overview" && (
        <OverviewTab
          team={team}
          setTab={setTab}
          teamPhoto={teamPhoto}
          members={membersProp ?? []}
          joinCode={joinCode}
          photos={photosProp ?? []}
          canManageJoinCode={canManageJoinCode}
          onGenerateJoinCode={onGenerateJoinCode}
          onDeleteJoinCode={onDeleteJoinCode}
        />
      )}
      {currentTab === "hunts" && <HuntsTab team={team} hunts={hunts} onNavigate={onNavigate} />}
      {currentTab === "map" && <MapTab pinCollection={pinCollection} team={team} onOpenEditor={openMapEditor} getAuthToken={getAuthToken} getPrintProbe={getPrintProbe} />}
      {currentTab === "heatmap" && heatmapEvents && <TeamHeatmapTab team={team} events={heatmapEvents} />}
      {currentTab === "statistics" && statisticsSection}
      {currentTab === "members" && <MembersTab team={team} joinCode={joinCode} initialMembers={membersProp} onChangeMemberRole={onChangeMemberRole} onRemoveMember={onRemoveMember} onInviteMember={onInviteMember} canManageJoinCode={canManageJoinCode} onGenerateJoinCode={onGenerateJoinCode} onDeleteJoinCode={onDeleteJoinCode} />}
      {currentTab === "documents" && <DocumentsTab team={team} initialDocTree={docTree} ops={ops} />}
      {currentTab === "pictures" && <PicturesTab photos={photosProp} truncated={photosTruncated} />}
      {currentTab === "settings" && (
        <SettingsTab
          teamName={teamName}
          onRename={onRenameTeam}
          teamPhoto={teamPhoto}
          onPhotoChange={onChangeTeamPhoto}
          onRemovePhoto={onRemoveTeamPhoto}
          canManage={canManageSettings}
          isOwner={team.role === "owner"}
          onDeleteTeam={onDeleteTeam}
          onLeaveTeam={canLeave ? onLeaveTeam : undefined}
        />
      )}
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────

/**
 * Full-screen photo preview. Opens over the Recent-photos slider and over the
 * Pictures gallery; the overlay mechanics live in MediaLightbox, which the
 * documents tab reuses for image documents.
 */
function PhotoLightbox({
  photos,
  index,
  onClose,
  onNavigate,
}: {
  photos: GroupPhoto[];
  index: number;
  onClose: () => void;
  onNavigate: (i: number) => void;
}) {
  const { t } = useTranslation('teams');
  return (
    <MediaLightbox
      items={photos.map((p) => ({
        url: p.url,
        type: p.type,
        // Stamped like the app's full-screen viewer: a trail-camera frame is
        // unidentifiable on its own, and answering "which camera, and when?"
        // is most of why anyone opens one.
        caption: formatPhotoStamp(p.createdAt, p.cameraName),
      }))}
      index={index}
      labels={{
        title: t('detail.lightbox.title'),
        close: t('detail.lightbox.close'),
        previous: t('detail.lightbox.previous'),
        next: t('detail.lightbox.next'),
      }}
      onClose={onClose}
      onNavigate={onNavigate}
    />
  );
}

function OverviewTab({
  team,
  setTab,
  teamPhoto,
  members,
  joinCode,
  photos,
  canManageJoinCode,
  onGenerateJoinCode,
  onDeleteJoinCode,
}: {
  team: Team
  setTab: (id: TabId) => void
  teamPhoto: string | null
  members: Member[]
  joinCode: string
  photos: GroupPhoto[]
  canManageJoinCode: boolean
  onGenerateJoinCode?: () => Promise<void>
  onDeleteJoinCode?: () => Promise<void>
}) {
  const { t } = useTranslation('teams');
  const roleLabel = useTeamRoleLabel();

  // CLUB-182: index of the photo open in the lightbox (null = closed).
  const [lightbox, setLightbox] = useState<number | null>(null);

  // Base tiles for the read-only preview below. This card mounts its own
  // instance of the shared presentation hook rather than reading tiles from
  // the Map tab: the two are siblings that may never both be on screen, and
  // the hook's module-level catalogue store means a second mount here costs
  // no extra `/api/maps` fetch. Only `tileSource` is used — no controls, no
  // panels, no `appearance` (see below).
  const mapPresentation = useTeamMapPresentation();

  // CLUB-206: read-only Overview map content (main border + pins).
  // Every parcel, not the first: a ground is routinely several, and reading
  // only [0] drew one parcel of it and called that the team boundary.
  const boundaryAreas = team.map.boundaryAreas ?? [];
  // The note is about the border specifically — a team with pins but no border
  // still has no ground drawn, and that is the thing worth pointing out.
  // A stored ring of one or two points is not a border, so the note now shows
  // for it where it previously did not.
  const hasBorder = boundaryAreas.some((a) => a.rings.some((r) => r.length >= 3));


  return (
    <div className="wh-grid-2col">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Hero */}
        <div className="wh-card" style={{ overflow: "hidden", position: "relative" }}>
          <div
            style={{
              height: 220,
              position: "relative",
              background: "linear-gradient(160deg, #1a3321 0%, var(--nl-primary) 60%, var(--nl-primary-container) 100%)",
            }}
          >
            {teamPhoto ? (
              <>
                <img
                  src={teamPhoto}
                  alt={team.name}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 60%)" }} />
              </>
            ) : (
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 800 220"
                preserveAspectRatio="xMidYMid slice"
                style={{ position: "absolute", inset: 0, opacity: 0.45 }}
              >
                {[...Array(14)].map((_, i) => (
                  <path
                    key={i}
                    d={`M ${-20 + i * 18} 220 L ${-20 + i * 18 + 30} ${80 + (i % 3) * 30} L ${-20 + i * 18 + 60} 220 Z`}
                    fill="#0a1e12"
                  />
                ))}
                {[...Array(20)].map((_, i) => (
                  <rect
                    key={"t" + i}
                    x={40 + i * 36}
                    y={60 + (i % 4) * 18}
                    width="6"
                    height={160 - (i % 4) * 18}
                    fill="#0a1e12"
                    opacity="0.7"
                  />
                ))}
                <circle cx="640" cy="70" r="60" fill="#fff" opacity="0.08" />
              </svg>
            )}
            <div
              style={{
                position: "absolute",
                bottom: 20,
                left: 24,
                right: 24,
                display: "flex",
                alignItems: "flex-end",
                gap: 16,
                // Fixed white, NOT COLORS.onPrimary: what sits behind this text
                // is the same dark strip in both themes — a group photo under a
                // dark scrim, and a team with no photo of its own still gets a
                // stock one — while on-primary inverts with the palette (white
                // on light, near-black green on dark). Reading it from the theme
                // therefore painted the hunting-ground name almost black onto a
                // dark photo, losing it entirely for dark-mode users.
                color: "#fff",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    opacity: 0.85,
                    marginBottom: 4,
                  }}
                >
                  {team.area}
                </div>
                <div
                  style={{
                    fontFamily: COLORS.fontDisplay,
                    fontSize: "clamp(22px, 6vw, 34px)",
                    fontWeight: 700,
                    lineHeight: 1.1,
                    overflowWrap: "anywhere",
                  }}
                >
                  {team.name}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(0,0,0,0.35)",
                  padding: "6px 10px",
                  borderRadius: 999,
                }}
              >
                <div style={{ display: "flex" }}>
                  {members.slice(0, 3).map((m, i) => (
                    <div
                      key={m.id}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 999,
                        background: avatarColor(m.id),
                        // AVATAR_PALETTE is six fixed dark shades, so the
                        // initials need a fixed light colour for the same reason
                        // the name above does — see the overlay's note.
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid rgba(0,0,0,0.35)",
                        marginLeft: i === 0 ? 0 : -6,
                      }}
                    >
                      {m.avatar}
                    </div>
                  ))}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{t('detail.overview.memberCount', { count: team.members })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* CLUB-206: read-only, pannable/zoomable map (main border + pins) with a
            link into the Map tab to edit. Renders the same base tiles as the
            Map tab (via useTeamMapPresentation's shared tileSource), but with
            no layer-picker or appearance controls of its own — this card has
            no panel to host them, and switching base maps or overlays stays a
            Map-tab action. The property-borders overlay is still deferred
            everywhere (needs cadastral data — tracked separately). */}
        <div className="wh-card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
            <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 18, fontWeight: 700, flex: 1 }}>
              {t('detail.overview.mapTitle')}
            </div>
            <button className="wh-btn wh-btn-text" onClick={() => setTab("map")}>
              {t('detail.overview.openMap')}
            </button>
          </div>
          {/* The map is worth showing even with nothing on it: it pans and
              zooms, so it is how someone finds their ground before drawing a
              border. Replacing it with a line of text left the reader with
              nothing to act on. Say what is missing instead, and keep the map.
              TeamMapView fits its opening view to the border via
              `initialBounds` (framing every ring's points, not just the
              first), and falls back to the regional overview position when
              there is no border to fit to — so it still renders something in
              every case, the same guarantee the old StandsMap viewport-seeding
              gave. */}
          {!hasBorder && (
            <p style={{ margin: "0 0 12px", fontSize: 14, color: COLORS.outline }}>
              {t('detail.overview.noBorders')}
            </p>
          )}
          <div style={{ height: 260, borderRadius: 8, overflow: "hidden", position: "relative" }} data-testid="team-overview-map">
            <TeamMapView map={team.map} interactive tileSource={mapPresentation.tileSource} />
          </div>
        </div>

        <div className="wh-card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
            <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 18, fontWeight: 700, flex: 1 }}>
              {t('detail.overview.recentPhotos')}
            </div>
            {photos.length > 0 && (
              <button className="wh-btn wh-btn-text" onClick={() => setTab("pictures")}>
                {t('detail.overview.seeAll', { count: photos.length })}
              </button>
            )}
          </div>
          {photos.length === 0 ? (
            <p style={{ margin: "4px 0 0", fontSize: 14, color: COLORS.outline }}>
              {t('detail.overview.noPhotos')}
            </p>
          ) : (
            // CLUB-182: horizontally scrollable slider; each photo opens the lightbox.
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
              {photos.map((p, i) => (
                <button
                  key={p.url}
                  type="button"
                  onClick={() => setLightbox(i)}
                  aria-label={t('detail.overview.openPhoto', { number: i + 1 })}
                  style={{
                    flex: "0 0 auto",
                    width: 96,
                    height: 96,
                    borderRadius: 8,
                    overflow: "hidden",
                    background: COLORS.surfaceContainerHighest,
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={p.thumbnail}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {lightbox !== null && (
          <PhotoLightbox
            photos={photos}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onNavigate={setLightbox}
          />
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 16 }}>
        <JoinCodeCard
          joinCode={joinCode}
          canManage={canManageJoinCode}
          label={t('detail.overview.joinCode')}
          hint={t('detail.overview.joinCodeHint')}
          padding={18}
          onGenerate={onGenerateJoinCode}
          onDelete={onDeleteJoinCode}
        />

        <div className="wh-card" style={{ padding: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.outline,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 10,
            }}
          >
            {t('detail.overview.nextHunt')}
          </div>
          {team.nextHunt ? (
            <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
              {team.nextHunt}
            </div>
          ) : (
            <div style={{ fontSize: 15, color: COLORS.outline, marginBottom: 4 }}>
              {t('noPlannedHunts')}
            </div>
          )}
          <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 14 }}>
            {team.map.name} · {team.area}
          </div>
          <button
            className="wh-btn wh-btn-primary"
            style={{ width: "100%" }}
            onClick={() => setTab("hunts")}
          >
            <Icon name="hunts" size={16} />
            {t('detail.overview.viewAllHunts')}
          </button>
        </div>

        <div className="wh-card" style={{ padding: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.outline,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 12,
            }}
          >
            {t('detail.overview.atAGlance')}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { l: t('detail.overview.glance.area'), v: `${team.hectares.toLocaleString()} ha` },
              { l: t('detail.overview.glance.cameras'), v: cameraCount(team.map.objects) },
              { l: t('detail.overview.glance.mapPins'), v: pinCount(team.map.objects) },
              { l: t('detail.overview.glance.yourRole'), v: roleLabel(team.role) },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", fontSize: 13 }}>
                <span style={{ color: COLORS.outline, flex: 1 }}>{s.l}</span>
                <span style={{ fontWeight: 700 }}>{s.v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="wh-card" style={{ padding: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.outline,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 12,
            }}
          >
            {t('detail.overview.quickActions')}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <QuickAction icon="map" label={t('detail.overview.openTeamMap')} onClick={() => setTab("map")} />
            <QuickAction icon="hunts" label={t('detail.overview.scheduleHunt')} onClick={() => setTab("hunts")} />
            <QuickAction icon="group" label={t('detail.overview.manageMembers')} onClick={() => setTab("members")} />
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon, label, onClick }: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 6,
        border: "none",
        background: "transparent",
        fontSize: 13,
        fontWeight: 600,
        color: COLORS.onSurface,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background: COLORS.primaryContainer,
          color: COLORS.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name={icon} size={14} />
      </div>
      <span style={{ flex: 1 }}>{label}</span>
      <Icon name="chevron-right" size={14} color={COLORS.outline} />
    </button>
  );
}

function PictureTile({
  photo,
  position,
  onOpen,
}: {
  photo: GroupPhoto;
  /** 1-based place in the gallery, which is the tile's accessible name. */
  position: number;
  onOpen?: () => void;
}) {
  const { t } = useTranslation('teams');
  const isVideo = photo.type === 'video';

  return (
    <div
      style={{
        aspectRatio: "4/3",
        borderRadius: 8,
        overflow: "hidden",
        position: "relative",
        background: "#1a3321",
      }}
    >
      <img
        src={photo.thumbnail}
        alt=""
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
      />
      {isVideo && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 40, height: 40, borderRadius: "50%",
              background: "rgba(0,0,0,0.55)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon name="play" size={18} />
          </div>
        </div>
      )}
      {/* The whole tile opens the preview. It is a transparent overlay button
          rather than a click handler on the wrapper so the affordance the
          pointer cursor promises is the same element that keyboard and screen
          readers reach. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('detail.overview.openPhoto', { number: position })}
        style={{
          position: "absolute", inset: 0, zIndex: 1,
          background: "transparent", border: "none", padding: 0,
          cursor: "pointer",
        }}
      />
    </div>
  );
}

// ─── Hunts tab ────────────────────────────────────────────────────────────

function HuntsTab({ team, hunts, onNavigate }: { team: Team; hunts: Hunt[]; onNavigate: (path: string) => void }) {
  const { t } = useTranslation('teams');
  const from = `/teams/${team.id}?tab=hunts`;
  const openHunt = (h: Hunt) => onNavigate(`/hunts/${h.id}?${new URLSearchParams(huntDetailSearchFor(h, from))}`);
  const teamHunts = hunts.filter((h) => h.teamId === team.id);
  const upcoming = teamHunts
    .filter((h) => h.status === "upcoming")
    .sort((a, b) => a.date.localeCompare(b.date));
  const ended = teamHunts
    .filter((h) => h.status === "ended")
    .sort((a, b) => b.date.localeCompare(a.date));
  const plannedCount = upcoming.length;

  return (
    <div className="wh-grid-2col-sm">
      <div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: "1 1 180px", minWidth: 0, fontSize: 14, color: COLORS.outline }}>
            {t('detail.hunts.plannedForPrefix', { count: plannedCount })}{" "}
            <b style={{ color: COLORS.onSurface }}>{team.name}</b>.
          </div>
          <button className="wh-btn wh-btn-outline" onClick={() => onNavigate('/hunts')}>
            <Icon name="hunts" size={16} />
            {t('detail.hunts.openCalendar')}
          </button>
          <button className="wh-btn wh-btn-primary" onClick={() => onNavigate(`/hunts/plan?teamId=${team.id}`)}>
            <Icon name="plus" size={16} />
            {t('detail.hunts.planHunt')}
          </button>
        </div>

        {upcoming.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
              <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 18, fontWeight: 500 }}>{t('detail.hunts.planned')}</div>
              <div style={{ fontSize: 13, color: COLORS.outline }}>{upcoming.length}</div>
            </div>
            <div className="wh-card" style={{ overflow: "hidden" }}>
              {upcoming.map((h, i) => (
                <HuntListRow
                  key={h.id}
                  hunt={h}
                  last={i === upcoming.length - 1}
                  onOpenInvite={openHunt}
                  onContinueDraft={() => onNavigate(`/hunts/plan?teamId=${team.id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {ended.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
              <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 18, fontWeight: 500 }}>{t('detail.hunts.ended')}</div>
              <div style={{ fontSize: 13, color: COLORS.outline }}>{ended.length}</div>
            </div>
            <div className="wh-card" style={{ overflow: "hidden" }}>
              {ended.map((h, i) => (
                <HuntListRow
                  key={h.id}
                  hunt={h}
                  last={i === ended.length - 1}
                  onOpenInvite={openHunt}
                  onContinueDraft={() => {}}
                />
              ))}
            </div>
          </div>
        )}

        {teamHunts.length === 0 && (
          <EmptyState title={t('detail.hunts.emptyTitle')} subtitle={t('detail.hunts.emptySubtitle')} icon="hunts" />
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <PendingInvitesRail hunts={hunts} teamId={team.id} onOpen={openHunt} />
        <DraftsRail hunts={hunts} teamId={team.id} onContinue={(h) => onNavigate(`/hunts/plan?draftId=${h.id}`)} onDiscard={() => {}} />
      </div>

    </div>
  );
}

function TeamHuntRow({
  hunt,
  last,
  onOpenInvite,
}: {
  hunt: Hunt;
  last: boolean;
  onOpenInvite: (h: Hunt) => void;
}) {
  const { t } = useTranslation('teams');
  const [y, m, d] = hunt.date.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.toLocaleDateString("en-GB", { weekday: "short" });
  const monthLabel = dt.toLocaleDateString("en-GB", { month: "short" });
  const ended = hunt.status === "ended";
  const rsvp = hunt.myRsvp;
  const rsvpChip =
    rsvp === "pending"
      ? { ...RSVP_COLORS.pending, label: t('detail.hunts.rsvp.pending') }
      : rsvp === "accepted"
      ? { ...RSVP_COLORS.accepted, label: t('detail.hunts.rsvp.attending') }
      : rsvp === "declined"
      ? { ...RSVP_COLORS.declined, label: t('detail.hunts.rsvp.declined') }
      : null;

  return (
    <div
      className="wh-hunt-row-grid"
      role="button"
      tabIndex={0}
      onClick={() => onOpenInvite(hunt)}
      onKeyDown={(e) => e.key === "Enter" && onOpenInvite(hunt)}
      style={{
        padding: "12px 16px",
        borderBottom: last ? "none" : `1px solid ${COLORS.surfaceContainerHighest}`,
        cursor: "pointer",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            fontSize: 11,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 700,
          }}
        >
          {dow}
        </div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 22, fontWeight: 500, lineHeight: 1 }}>{d}</div>
        <div
          style={{
            fontSize: 11,
            color: COLORS.outline,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {monthLabel}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {hunt.quarry} · {hunt.location}
        </div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {hunt.start}–{hunt.end} · {t('detail.hunts.participants', { count: hunt.participants })}
        </div>
      </div>
      {ended ? (
        <span className="wh-chip muted">{t('detail.hunts.endedChip')}</span>
      ) : rsvpChip ? (
        <button
          className="wh-chip"
          style={{ background: rsvpChip.bg, color: rsvpChip.fg, borderColor: "transparent", cursor: "pointer" }}
          onClick={(e) => { e.stopPropagation(); onOpenInvite(hunt); }}
        >
          {rsvpChip.label}
          {rsvp === "pending" ? " →" : ""}
        </button>
      ) : (
        <span className="wh-chip">{t('detail.hunts.upcomingChip')}</span>
      )}
    </div>
  );
}

// ─── Map tab ──────────────────────────────────────────────────────────────

/**
 * CLUB-676: the height that makes the Map tab's card reach the bottom of the
 * window, as a CSS variable on the card.
 *
 * Measured rather than expressed in CSS because nothing above the card has a
 * definite height to fill against — the app shell is content-sized, so
 * `height: 100%` there resolves to whatever the content already is. And the
 * chrome above the card is not a constant, so `calc(100dvh - 420px)` is wrong
 * on any page where the banner is dismissed or the team name wraps.
 *
 * The card's own top does NOT depend on the card's height, so setting the
 * height from it cannot feed back — but the measurement is taken against the
 * document, not the viewport, so a scrolled page still computes the same value
 * instead of shrinking the card as the user scrolls.
 */
function useFillViewport(ref: React.RefObject<HTMLDivElement | null>) {
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      // The gap keeps the card off the very bottom edge; the floor is what
      // stops a short window crushing the map, letting the page scroll instead.
      setHeight(Math.max(420, Math.round(window.innerHeight - top - 24)));
    };
    measure();
    window.addEventListener('resize', measure);
    // The chrome above can change height without the window resizing — the
    // banner being dismissed, or the title rewrapping.
    const ro = new ResizeObserver(measure);
    if (el.parentElement) ro.observe(el.parentElement);
    // A web font arriving after mount reflows the title above and moves the
    // card's top without resizing the observed parent, so the first measurement
    // goes stale by the height of one line's font swap. Measure again once the
    // fonts have settled. Optional: jsdom has no `document.fonts`.
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [ref]);
  return height;
}

function MapTab({ team, onOpenEditor, getAuthToken, getPrintProbe, pinCollection }: { team: Team; onOpenEditor: () => void; getAuthToken?: () => Promise<string | null>; getPrintProbe?: PrintProbeLookup; pinCollection?: PinCollectionOps }) {
  const { t } = useTranslation('teams');
  // Which full-screen surface is up, if any. One piece of state rather than two
  // booleans: the plain preview and the print view are both full-screen dialogs
  // and must never be open at the same time.
  const [overlay, setOverlay] = useState<"view" | "print" | null>(null);
  // Layers + view settings here too, not only in the print surface: choosing a
  // base map or turning a layer on is how you read the ground, not just how you
  // print it. Appearance is shared, so a change here holds in the full-screen
  // preview, the print surface and the editor.
  const presentation = useTeamMapPresentation();
  // Server-rendered PDF is the only print path now (ratified product call,
  // 2026-09-01): a Print button that opens a dialog which cannot actually
  // print — no auth token to exchange a print session with, or a ground with
  // no centre to frame a sheet around — is worse than no button at all, so
  // both Print entry points (this tab's button and the full-screen preview's)
  // stay hidden rather than inviting a click that leads nowhere.
  //
  // Where the ground SITS no longer enters into it. The print server used to
  // refuse everything outside Finland, Sweden and France, so the button was
  // gated on a set of bounding boxes that could only ever approximate those
  // borders; it now falls back to the worldwide raster tiles instead, which
  // makes every ground printable and the boxes' wrong answers — a Danish
  // ground refused, a Belgian one offered — pure loss.
  const groundCenter = useMemo(() => groundCenterForPrint(team.map), [team.map]);
  const canPrint = getAuthToken !== undefined && groundCenter !== null;
  usePrintShortcut(() => setOverlay("print"), canPrint && overlay !== "print");
  // CLUB-676: the folder tree's centre-on-map request. A nonce, not just a
  // point, so clicking the same pin twice — with a pan in between — brings you
  // back to it; see TeamMapView's `focus` prop.
  const [focus, setFocus] = useState<{ lat: number; lon: number; nonce: number } | null>(null);
  // Whether the folder list is showing. OPEN by default: which folder a stand
  // is filed in, and whether an imported collection arrived, is what the map
  // is being read for, and the tab said nothing about the list existing — a
  // folder glyph on a count chip is not a discoverable affordance. The chip
  // still closes it, handing the full width back to the map.
  const [pinsOpen, setPinsOpen] = useState(true);
  const cardRef = useRef<HTMLDivElement>(null);
  const collectionRef = useRef<HTMLDivElement>(null);
  const fillHeight = useFillViewport(cardRef);
  // The map fills the window, so a card below it opens off the fold. Bring it
  // to the reader rather than leaving the click looking like it did nothing.
  //
  // Only on a closed → open transition, never on first paint: the list starts
  // open, and scrolling to it as the tab renders would land the reader below
  // the map they came to see. Seeding the ref with the initial value is what
  // makes that mount a non-transition — and keeps it one under a StrictMode
  // double-mount, which a "have I run before" flag would not.
  const pinsWasOpen = useRef(pinsOpen);
  useEffect(() => {
    const justOpened = pinsOpen && !pinsWasOpen.current;
    pinsWasOpen.current = pinsOpen;
    if (!justOpened) return;
    // Optional call: jsdom has no scrollIntoView, and a missing scroll must
    // never take the panel down with it.
    collectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [pinsOpen]);
  // CLUB-676: what the viewer has hidden, as the canvas needs it. Hiding a pin
  // in the list has to take it OFF the map — a row that dims while its sprite
  // stays put is the list and the map disagreeing about the same pin, which is
  // exactly the confusion this ticket set out to remove.
  //
  // The collection's master switch hides everything it holds rather than being
  // a fourth thing the canvas has to know about, so there is one rule here:
  // "not drawn" is one set of ids.
  const vis = pinCollection?.visibility;
  // The pin collection, as the list shows it: the pin documents and the
  // hunter's own, without the trail cameras. A camera is drawn on this map but
  // cannot be filed, renamed, exported or deleted as a pin, and it is counted
  // by the Cameras chip. A camera is hidden from the map by its pin TYPE, in
  // the appearance panel.
  //
  // The hunter's own pins ARE listed, and the panel labels them as theirs
  // rather than folding them into a ground total — that is what the Pins chip
  // beside it deliberately excludes (see `pinCount`), so a single number
  // covering both is a number no reader can check. The panel splits them into
  // "The team's pins" and "My pins", each with its own count.
  const listedPins = useMemo(
    () => team.map.objects.filter((o) => !isTrailCameraPin(o)),
    [team.map.objects],
  );
  const hiddenObjectIds = useMemo(() => {
    if (!vis) return undefined;
    if (!vis.collectionHidden) return vis.hiddenPinIds;
    // Exactly what the list holds — the master switch is that list's own
    // heading control, so it must not reach past it to something the panel
    // never showed.
    return new Set([...listedPins.map((o) => o.id), ...(team.map.lines ?? []).map((l) => l.id)]);
  }, [vis, listedPins, team.map.lines]);
  return (
    <>
    <div
      ref={cardRef}
      className="wh-card wh-map-card"
      style={{ overflow: "hidden", ...(fillHeight ? { ['--wh-map-fill' as string]: `${fillHeight}px` } : {}) } as CSSProperties}
    >
      {/* The summary bar sits ABOVE the map. Below it, the area size and the
          "Edit map" action were pushed under a tall map — off-screen on a
          laptop viewport, so the primary action on this tab had to be
          scrolled to before it could be found. */}
      <div className="wh-map-summary-bar">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 17, fontWeight: 600, lineHeight: 1.2 }}>
            {team.hectares.toLocaleString()} ha
          </div>
          <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>
            {t('detail.map.lastEdited', { date: team.map.updated })}
          </div>
        </div>
        <div className="wh-map-stat-chips">
          <MapStatChip
            icon="pin"
            label={t('detail.map.pins')}
            value={pinCount(team.map.objects)}
            active={pinsOpen}
            onClick={() => setPinsOpen((open) => !open)}
            title={pinsOpen ? t('detail.map.hidePins') : t('detail.map.showPins')}
          />
          <MapStatChip icon="camera" label={t('detail.map.cameras')} value={cameraCount(team.map.objects)} />
          <MapStatChip icon="subarea" label={t('detail.map.subAreas')} value={team.map.subAreaPolygons?.length ?? 0} />
          <MapStatChip icon="forbidden-area" label={t('detail.map.forbidden')} value={team.map.forbiddenPolygons?.length ?? 0} />
        </div>
        {/* Printing is a read action — it sits outside the CLUB-175 `canEdit`
            gate, like the full-screen preview and document downloads, so a
            view-only member can still take the map to paper. Getting to it used
            to mean entering the editor, which they cannot do. Gated on canPrint
            regardless: view-only widens WHO can print, it does not excuse
            WHETHER printing can succeed here at all. */}
        {/* `wh-btn-sm`: the summary bar is a row of chips reporting counts, and at
            the default 40px these two read as the loudest thing on the tab. The
            small size is the one already used for row-level actions elsewhere. */}
        <div className="wh-map-bar-actions">
          {canPrint && (
            <button
              type="button"
              onClick={() => setOverlay("print")}
              className="wh-btn wh-btn-outline wh-btn-sm"
            >
              <Icon name="print" size={14} />
              {t('detail.map.print')}
            </button>
          )}
          {team.canEdit && (
            <button onClick={onOpenEditor} className="wh-btn wh-btn-primary wh-btn-sm">
              <Icon name="map" size={14} />
              {t('detail.map.editMap')}
            </button>
          )}
        </div>
      </div>
      <div className="wh-map-overview" style={{ position: "relative" }}>
        <TeamMapView
          map={team.map}
          interactive={true}
          appearance={presentation.appearance}
          tileSource={presentation.tileSource}
          overlayLayers={presentation.overlayLayers}
          extraControls={presentation.controls}
          focus={focus}
          hiddenObjectIds={hiddenObjectIds}
        />
        <button
          type="button"
          className="wh-btn-icon"
          aria-label={t('detail.map.openFullscreen')}
          title={t('detail.map.fullscreen')}
          onClick={() => setOverlay("view")}
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 4,
            background: COLORS.surface,
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          }}
        >
          <Icon name="fullscreen" size={20} />
        </button>
        {presentation.panels}
      </div>
      </div>

      {/* CLUB-676: the collection is its own card UNDER the map, not a column
          inside it. The map keeps the full width it fills the window with, and
          the list gets a width worth reading a folder name in.
          It scrolls itself into view on open — a container that appears below
          the fold is, to the person who just clicked for it, nothing
          happening. */}
      {pinsOpen && (
        <div ref={collectionRef} className="wh-card wh-map-pin-card">
          <button
            type="button"
            className="wh-btn-icon"
            onClick={() => setPinsOpen(false)}
            title={t('detail.map.hidePins')}
            aria-label={t('detail.map.hidePins')}
            style={{ position: "absolute", top: 10, right: 8, zIndex: 3 }}
          >
            <Icon name="close" size={18} />
          </button>
          <PinFolderTree
            folders={team.map.pinFolders ?? []}
            pins={listedPins}
            lines={team.map.lines}
            onFocus={(pt) => setFocus({ ...pt, nonce: Date.now() })}
            {...pinCollection}
          />
        </div>
      )}

      {overlay === "view" && (
        <TeamMapFullscreen
          team={team}
          hiddenObjectIds={hiddenObjectIds}
          onClose={() => setOverlay(null)}
          // Swaps overlay rather than stacking a second dialog on the first.
          onPrint={() => setOverlay("print")}
          canPrint={canPrint}
        />
      )}
      {overlay === "print" && (
        <TeamMapPrintView
          map={team.map}
          teamId={team.id}
          teamName={team.name}
          getAuthToken={getAuthToken}
          getPrintProbe={getPrintProbe}
          // What the tab is hiding stays hidden on paper, as it does in the
          // full-screen preview.
          hiddenObjectIds={hiddenObjectIds}
          onClose={() => setOverlay(null)}
        />
      )}
    </>
  );
}

// CLUB-208: full-screen, read-only, movable preview of the team map. Reuses the
// same interactive TeamMapView as the Map tab so pan/zoom + rendering stay identical;
// editing stays behind "Edit map".
function TeamMapFullscreen({
  team,
  onClose,
  onPrint,
  canPrint,
  hiddenObjectIds,
}: {
  team: Team;
  onClose: () => void;
  /** CLUB-575: print has to be reachable from the preview, not only the Map tab. */
  onPrint: () => void;
  /**
   * Whether the server can actually print this ground for this session —
   * computed once by MapTab (see its comment) and passed down rather than
   * re-derived here, since both entry points must agree on the same answer.
   */
  canPrint: boolean;
  /**
   * CLUB-676: forwarded so the enlargement shows the same map. This is the Map
   * tab's own view at full size, and a pin the reader hid reappearing the
   * moment they expand it would read as a fault.
   */
  hiddenObjectIds?: ReadonlySet<string>;
}) {
  const { t } = useTranslation('teams');
  const closeRef = useRef<HTMLButtonElement>(null);
  const presentation = useTeamMapPresentation();

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('detail.map.previewTitle')}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "#000",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <TeamMapView
          map={team.map}
          interactive={true}
          appearance={presentation.appearance}
          tileSource={presentation.tileSource}
          overlayLayers={presentation.overlayLayers}
          hiddenObjectIds={hiddenObjectIds}
          extraControls={
            <>
              {presentation.controls}
              {/* Same server-PDF-only gate as the Map tab's own Print button
                  (see MapTab's comment) — this preview must not offer a print
                  path the tab itself would refuse. */}
              {canPrint && (
                <SurfaceIconButton title={t('detail.map.print')} onClick={onPrint}>
                  <Icon name="print" size={20} />
                </SurfaceIconButton>
              )}
            </>
          }
        />
        {presentation.panels}
        <button
          ref={closeRef}
          type="button"
          className="wh-btn-icon"
          aria-label={t('detail.map.closeFullscreen')}
          title={t('detail.common.close')}
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            // Anchor to the top-LEFT: the interactive TeamMapView renders its
            // zoom/recenter control stack at top-right, so a top-right close
            // button overlaps and hides the '+' zoom control (CLUB-309).
            left: 16,
            zIndex: 5,
            background: COLORS.surface,
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          }}
        >
          <Icon name="close" size={24} />
        </button>
      </div>
    </div>
  );
}

function MapStatChip({
  icon,
  label,
  value,
  onClick,
  active,
  title,
}: {
  icon: IconName;
  label: string;
  value: string | number;
  /**
   * CLUB-676: makes the chip a control. Only the Pins chip takes one — it is
   * the way back to the folder list once the list has been closed. The rest
   * stay plain counts, so a chip that does nothing never looks pressable.
   */
  onClick?: () => void;
  /** Whether the thing this chip opens is currently showing. */
  active?: boolean;
  title?: string;
}) {
  const body = (
    <>
      <span style={{ color: COLORS.primary, display: "inline-flex" }}>
        <Icon name={icon} size={15} />
      </span>
      <span style={{ fontSize: 14, fontWeight: 700 }}>{value}</span>
      <span style={{ fontSize: 12, color: COLORS.outline }}>{label}</span>
    </>
  );
  const shell: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "6px 11px",
    background: active ? COLORS.primaryContainer : COLORS.surface,
    borderRadius: 8,
  };

  if (!onClick) return <div style={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      style={{
        ...shell,
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        color: "inherit",
      }}
    >
      {body}
      {/* A folder, not a chevron. A left/right chevron beside a count reads as
          paging through something, and neither direction says what it would
          bring — where a folder names the thing on the other side of the click
          and matches both the editor's own way in and the glyphs down the
          list. */}
      <Icon name={active ? "folder_open" : "folder"} size={16} color={COLORS.outline} />
    </button>
  );
}

// ─── Members tab ──────────────────────────────────────────────────────────

function MembersTab({ team, joinCode, initialMembers, onChangeMemberRole, onRemoveMember, onInviteMember, canManageJoinCode, onGenerateJoinCode, onDeleteJoinCode }: { team: Team; joinCode: string; initialMembers?: Member[]; onChangeMemberRole?: (memberId: string, roleId: MemberRoleId) => Promise<void>; onRemoveMember?: (memberId: string) => Promise<void>; onInviteMember?: (input: { email: string; phone: string; country: string }) => Promise<{ ok: boolean; error?: string }>; canManageJoinCode: boolean; onGenerateJoinCode?: () => Promise<void>; onDeleteJoinCode?: () => Promise<void> }) {
  const { t } = useTranslation('teams');
  const roles = useMemberRoles();
  const [members, setMembers] = useState<Member[]>(initialMembers ?? []);

  useEffect(() => {
    if (initialMembers !== undefined) {
      setMembers(initialMembers);
    }
  }, [initialMembers]);

  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showInvite, setShowInvite] = useState(false);

  const owner = members.find((m) => m.owner);
  const rest = members.filter((m) => !m.owner);
  const filtered = query
    ? rest.filter((m) => m.name.toLowerCase().includes(query.toLowerCase()))
    : rest;
  const admins = filtered.filter((m) => m.roleId === "admin").sort((a, b) => a.name.localeCompare(b.name));
  const regular = filtered.filter((m) => m.roleId !== "admin").sort((a, b) => a.name.localeCompare(b.name));

  // CLUB-196: only Owner / Administrator can modify members' roles. For non-admins
  // the per-row edit control (the only entry to the role editor) is hidden, so
  // they can't change anyone's role — including their own.
  const canManageRoles = team.role === "owner" || team.role === "admin";
  // Inviting is held to the same roles: the API answers anyone else with 403.
  const canInvite = onInviteMember != null && canManageRoles;

  const updateRole = (id: string, roleId: MemberRoleId) => {
    if (!canManageRoles) return;
    // CLUB-174: optimistic update for a snappy UI, then persist. If the backend
    // rejects, revert to the previous role so the list reflects the real state.
    const prev = members.find((m) => m.id === id)?.roleId;
    setMembers((list) => list.map((m) => (m.id === id ? { ...m, roleId } : m)));
    setEditing(null);
    if (onChangeMemberRole && prev !== undefined && prev !== roleId) {
      onChangeMemberRole(id, roleId).catch(() => {
        setMembers((list) => list.map((m) => (m.id === id ? { ...m, roleId: prev } : m)));
      });
    }
  };
  const removeMember = (id: string) => {
    // Snapshot the current list so a failed backend removal can be rolled back
    // (mirrors changeRole above). Previously this filtered local state ONLY and
    // called nothing, so the member vanished from the screen but was never
    // removed on the server — a page refresh brought them back.
    const snapshot = members;
    setMembers((list) => list.filter((m) => m.id !== id));
    setDeleting(null);
    if (onRemoveMember) {
      onRemoveMember(id).catch(() => setMembers(snapshot));
    }
  };

  const editingMember = members.find((m) => m.id === editing);
  const deletingMember = members.find((m) => m.id === deleting);

  return (
    <div className="wh-grid-2col">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: COLORS.outline,
              }}
            >
              <Icon name="search" size={16} />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('detail.members.searchPlaceholder')}
              style={{
                width: "100%",
                padding: "9px 12px 9px 34px",
                fontSize: 13,
                border: `1.5px solid ${COLORS.surfaceContainerHighest}`,
                borderRadius: 6,
                outline: "none",
                background: COLORS.surfaceContainerLowest,
              }}
            />
          </div>
          {/* Only offered when a caller wires up onInviteMember (the app gates it on
              the invite transport being live) and only to the Owner or an
              Administrator: the API refuses anyone else, so a plain member who typed
              an address got nothing but a generic "could not send" error. */}
          {canInvite && (
            <button className="wh-btn wh-btn-primary" onClick={() => setShowInvite(true)}>
              <Icon name="plus" size={16} />
              {t('detail.members.inviteMembers')}
            </button>
          )}
        </div>

        {owner && (
          <MemberSection title={t('detail.members.owner')} count={1}>
            <MemberRow member={owner} readOnly />
          </MemberSection>
        )}
        {admins.length > 0 && (
          <MemberSection title={t('detail.members.administrators')} count={admins.length}>
            {admins.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                readOnly={!canManageRoles}
                onEdit={() => setEditing(m.id)}
                onDelete={() => setDeleting(m.id)}
              />
            ))}
          </MemberSection>
        )}
        {regular.length > 0 && (
          <MemberSection title={t('detail.members.members')} count={regular.length}>
            {regular.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                readOnly={!canManageRoles}
                onEdit={() => setEditing(m.id)}
                onDelete={() => setDeleting(m.id)}
              />
            ))}
          </MemberSection>
        )}

        {filtered.length === 0 && query && (
          <div
            className="wh-card"
            style={{ padding: 32, textAlign: "center", color: COLORS.outline, fontSize: 14 }}
          >
            {t('detail.members.noMatchPrefix')} <strong style={{ color: COLORS.onSurface }}>&quot;{query}&quot;</strong>.
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <JoinCodeCard
          joinCode={joinCode}
          canManage={canManageJoinCode}
          label={t('detail.members.joinCode')}
          hint={t('detail.members.joinCodeHint')}
          padding={20}
          onGenerate={onGenerateJoinCode}
          onDelete={onDeleteJoinCode}
        />

        <div className="wh-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{t('detail.members.rolesTitle')}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {roles.map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 10 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    flexShrink: 0,
                    background: r.id === "admin" ? COLORS.primaryContainer : COLORS.surfaceContainer,
                    color: r.id === "admin" ? COLORS.primary : COLORS.outline,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name={r.id === "admin" ? "shield" : r.id === "edit" ? "edit" : "eye"} size={16} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: COLORS.outline, lineHeight: 1.45 }}>{r.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editingMember && (
        <MemberEditModal
          member={editingMember}
          onCancel={() => setEditing(null)}
          onSave={(roleId) => updateRole(editingMember.id, roleId)}
          onDelete={() => {
            setEditing(null);
            setDeleting(editingMember.id);
          }}
        />
      )}

      {deletingMember && (
        <ConfirmModal
          title={t('detail.members.removeTitle', { name: deletingMember.name })}
          body={t('detail.members.removeBody', { name: deletingMember.name, team: team.name })}
          onCancel={() => setDeleting(null)}
          onConfirm={() => removeMember(deletingMember.id)}
        />
      )}

      {showInvite && canInvite && (
        <InviteByEmailOrPhoneModal
          onClose={() => setShowInvite(false)}
          onInvite={onInviteMember}
          labels={{
            title: t('detail.invite.title'),
            subtitle: t('detail.invite.subtitle'),
            email: t('detail.invite.email'),
            orWith: t('detail.invite.orWith'),
            country: t('detail.invite.country'),
            number: t('detail.invite.number'),
            add: t('detail.invite.add'),
            sending: t('detail.invite.sending'),
            cancel: t('detail.common.cancel'),
          }}
        />
      )}
    </div>
  );
}

function MemberSection({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <div className="wh-card" style={{ overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 18px",
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: COLORS.outline,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.outline,
            background: COLORS.primaryContainer,
            padding: "1px 8px",
            borderRadius: 999,
          }}
        >
          {count}
        </div>
      </div>
      <div className="wh-member-list">{children}</div>
    </div>
  );
}

function MemberRow({
  member,
  onEdit,
  onDelete,
  readOnly,
}: {
  member: Member;
  onEdit?: () => void;
  onDelete?: () => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('teams');
  const roles = useMemberRoles();
  const meta = roles.find((r) => r.id === member.roleId) || roles[0];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 18px",
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      <Avatar initials={member.avatar} photo={member.photo} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
          <span style={{ overflowWrap: "anywhere" }}>{member.name}</span>
          {member.you && (
            <span style={{ fontWeight: 500, color: COLORS.outline, fontSize: 12, flexShrink: 0 }}>{t('detail.members.you')}</span>
          )}
          {member.owner && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                padding: "2px 6px",
                borderRadius: 3,
                flexShrink: 0,
                background: COLORS.tertiaryContainer,
                // Pair the container with its own on-colour. `onTertiaryFixedVariant`
                // is #a03c16 in BOTH themes — identical to `tertiaryContainer` on
                // dark, giving orange-on-orange. `onTertiaryContainer` adapts.
                color: COLORS.onTertiaryContainer,
              }}
            >
              {t('detail.members.ownerBadge')}
            </span>
          )}
        </div>
      </div>
      <span className={`wh-chip ${meta.id === "admin" ? "" : "muted"}`} style={{ flexShrink: 0 }}>{meta.short}</span>
      {!readOnly && (
        <button className="wh-btn-icon" onClick={onEdit} title={t('detail.members.editMember')} style={{ flexShrink: 0 }}>
          <Icon name="edit" size={16} />
        </button>
      )}
      {readOnly && <div style={{ width: 36 }} />}
    </div>
  );
}

function MemberEditModal({
  member,
  onCancel,
  onSave,
  onDelete,
}: {
  member: Member;
  onCancel: () => void;
  onSave: (id: MemberRoleId) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('teams');
  const roles = useMemberRoles();
  const [roleId, setRoleId] = useState<MemberRoleId>(member.roleId);
  return (
    <ModalShell onCancel={onCancel} maxWidth={520}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "18px 22px",
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        }}
      >
        <Avatar initials={member.avatar} photo={member.photo} size={40} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
            {member.name}
          </div>
        </div>
        <button className="wh-btn-icon" onClick={onCancel}>
          <Icon name="close" size={18} />
        </button>
      </div>
      <div style={{ padding: "16px 22px 4px" }}>
        <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 14, lineHeight: 1.5 }}>
          {t('detail.members.editModalHint')}
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: COLORS.outline,
            marginBottom: 10,
          }}
        >
          {t('detail.members.roleLabel')}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
          {roles.map((r) => {
            const active = roleId === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setRoleId(r.id)}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "12px 14px",
                  border: `1.5px solid ${active ? COLORS.primary : COLORS.surfaceContainerHighest}`,
                  borderRadius: 8,
                  background: active ? COLORS.primaryContainer : COLORS.surfaceContainerLowest,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    flexShrink: 0,
                    marginTop: 2,
                    border: `2px solid ${active ? COLORS.primary : COLORS.outlineVariant}`,
                    background: COLORS.surfaceContainerLowest,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {active && (
                    <div style={{ width: 10, height: 10, borderRadius: 999, background: COLORS.primary }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: COLORS.outline, lineHeight: 1.45 }}>{r.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 22px",
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
        }}
      >
        <button className="wh-btn wh-btn-danger" onClick={onDelete}>
          <Icon name="trash" size={14} />
          {t('detail.members.removeFromTeam')}
        </button>
        <div style={{ flex: 1 }} />
        <button className="wh-btn wh-btn-outline" onClick={onCancel}>
          {t('detail.common.cancel')}
        </button>
        <button
          className="wh-btn wh-btn-primary"
          onClick={() => onSave(roleId)}
          disabled={roleId === member.roleId}
        >
          {t('detail.common.save')}
        </button>
      </div>
    </ModalShell>
  );
}

function ConfirmModal({
  title,
  body,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('teams');
  return (
    <ModalShell onCancel={onCancel} maxWidth={460}>
      <div style={{ padding: "22px 24px 6px" }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 22, fontWeight: 500, marginBottom: 8 }}>
          {title}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, lineHeight: 1.55 }}>{body}</div>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          padding: "14px 24px",
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
        }}
      >
        <button className="wh-btn wh-btn-outline" onClick={onCancel}>
          {t('detail.common.cancel')}
        </button>
        <button className="wh-btn wh-btn-danger" onClick={onConfirm}>
          {t('detail.common.confirm')}
        </button>
      </div>
    </ModalShell>
  );
}

// CLUB-500: the team join-code card. Owner/Administrator-only (members and
// view-only users never see it). When a code exists it shows the code with copy
// + delete; once deleted it offers a Generate action. Deleting revokes joining
// until a new code is generated (both are server-enforced).
function JoinCodeCard({
  joinCode,
  canManage,
  label,
  hint,
  padding,
  onGenerate,
  onDelete,
}: {
  joinCode: string;
  canManage: boolean;
  label: string;
  hint: string;
  padding: number;
  onGenerate?: () => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const { t } = useTranslation('teams');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Owner/admin only — hidden entirely for members / view-only.
  if (!canManage) return null;

  const hasCode = joinCode.trim().length > 0;

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(joinCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — the code stays visible
      // to copy by hand; nothing to recover from.
    }
  };

  const runGenerate = async () => {
    if (!onGenerate || busy) return;
    setBusy(true);
    try { await onGenerate(); } finally { setBusy(false); }
  };

  const runDelete = async () => {
    if (!onDelete || busy) return;
    setConfirmingDelete(false);
    setBusy(true);
    try { await onDelete(); } finally { setBusy(false); }
  };

  return (
    <div className="wh-card" style={{ padding }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </div>
        {hasCode && onDelete && (
          <button
            className="wh-btn wh-btn-text"
            style={{ color: COLORS.error, padding: "2px 6px" }}
            onClick={() => setConfirmingDelete(true)}
            disabled={busy}
            aria-label={t('detail.joinCode.deleteLabel')}
          >
            <Icon name="trash" size={16} />
          </button>
        )}
      </div>

      {hasCode ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            <div
              style={{
                fontFamily: COLORS.fontDisplay,
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: 2,
                color: COLORS.primary,
              }}
            >
              {joinCode}
            </div>
            <button
              className="wh-btn wh-btn-text"
              style={{ padding: "2px 6px" }}
              onClick={copy}
              aria-label={t('detail.joinCode.copyLabel')}
            >
              <Icon name={copied ? "check" : "copy"} size={16} />
              {copied ? t('detail.joinCode.copied') : t('detail.joinCode.copy')}
            </button>
          </div>
          <div style={{ fontSize: 12, color: COLORS.outline, lineHeight: 1.5 }}>{hint}</div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: COLORS.outline, lineHeight: 1.5, marginBottom: 12 }}>
            {t('detail.joinCode.emptyHint')}
          </div>
          {onGenerate && (
            <button className="wh-btn wh-btn-primary" onClick={runGenerate} disabled={busy}>
              <Icon name="refresh" size={16} />
              {t('detail.joinCode.generate')}
            </button>
          )}
        </>
      )}

      {confirmingDelete && (
        <DeleteJoinCodeModal
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={runDelete}
        />
      )}
    </div>
  );
}

// CLUB-500: a plain confirm for deleting the join code. Not type-to-confirm like
// team deletion — deleting a code is reversible (regenerate), so a single
// confirm is the right weight.
function DeleteJoinCodeModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('teams');
  return (
    <ModalShell onCancel={onCancel} maxWidth={440}>
      <div style={{ padding: "22px 24px 6px" }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 22, fontWeight: 500, marginBottom: 8 }}>
          {t('detail.joinCode.deleteModal.title')}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, lineHeight: 1.55, marginBottom: 16 }}>
          {t('detail.joinCode.deleteModal.body')}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          padding: "16px 24px",
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
        }}
      >
        <button className="wh-btn wh-btn-outline" onClick={onCancel}>
          {t('detail.common.cancel')}
        </button>
        <button className="wh-btn wh-btn-danger" onClick={onConfirm}>
          {t('detail.common.delete')}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  onCancel,
  maxWidth,
  children,
}: {
  onCancel: () => void;
  maxWidth: number;
  children: ReactNode;
}) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "rgba(10,20,15,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surfaceContainerLowest,
          borderRadius: 12,
          width: "100%",
          maxWidth,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Pictures tab ─────────────────────────────────────────────────────────

/**
 * The team's trail-camera gallery. Read-only by design: this media lives in the
 * camera owner's Burrel account, shared into the team, and the wire protocol has
 * no per-team exclusion — the only per-photo delete it offers is a soft-delete
 * that removes the photo for the owner and for every other team the camera is
 * shared with. Nothing in WeHunt writes that field: mobile's trail-camera
 * feature reads it and offers no delete either. So this tab shows pictures and
 * opens them, and removing one is done in Burrel, where the photo lives.
 */
function PicturesTab({ photos, truncated }: { photos?: GroupPhoto[]; truncated?: boolean }) {
  const { t } = useTranslation('teams');
  // Index into visiblePhotos of the photo open in the full-screen preview
  // (null = closed) — the same lightbox the Overview slider uses.
  const [lightbox, setLightbox] = useState<number | null>(null);

  const visiblePhotos = photos ?? [];

  if (photos !== undefined && visiblePhotos.length === 0) {
    return (
      <EmptyState
        title={t('detail.pictures.emptyTitle')}
        subtitle={t('detail.pictures.emptySubtitle')}
        icon="pictures"
      />
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <div style={{ flex: 1, fontSize: 14, color: COLORS.outline }}>
          {truncated
            ? t('detail.pictures.galleryCountTruncated', { count: visiblePhotos.length })
            : t('detail.pictures.galleryCount', { count: visiblePhotos.length })}
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 12,
        }}
      >
        {visiblePhotos.map((p, i) => (
          <div key={p.url} className="wh-card" style={{ overflow: "hidden", padding: 0 }}>
            <PictureTile
              photo={p}
              position={i + 1}
              onOpen={() => setLightbox(i)}
            />
            <div style={{ padding: "10px 12px" }}>
              <div style={{ fontSize: 11, color: COLORS.outline }}>
                {new Date(p.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              </div>
            </div>
          </div>
        ))}
      </div>
      {lightbox !== null && (
        <PhotoLightbox
          photos={visiblePhotos}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
        />
      )}
    </div>
  );
}

// ─── Settings tab ─────────────────────────────────────────────────────────

function SettingsTab({ teamName, onRename, teamPhoto, onPhotoChange, onRemovePhoto, canManage, isOwner, onDeleteTeam, onLeaveTeam }: {
  teamName: string; onRename?: (name: string) => Promise<void>;
  teamPhoto: string | null;
  onPhotoChange?: (file: File) => Promise<void>;
  onRemovePhoto?: () => Promise<void>;
  /** Owner or Administrator: offered the team picture. A plain member sees only the danger zone. */
  canManage: boolean;
  /** The current user owns this team, and so is the only role offered the delete. */
  isOwner: boolean;
  /** Owner-only delete of the whole team. Rejects when the delete failed, which the confirmation shows. */
  onDeleteTeam?: () => void | Promise<void>;
  /** Leave the team. Supplied for every role except the Owner. */
  onLeaveTeam?: () => void;
}) {
  const { t } = useTranslation('teams');
  const [draft, setDraft] = useState(teamName);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dirty = draft.trim() !== teamName;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (!onRename || !dirty || !draft.trim() || saving) return;
    setSaving(true);
    try {
      await onRename(draft.trim());
    } catch {
      // The container reports the failure to the user. Nothing to do here but
      // clear the pending flag so the field is editable again — `dirty` stays
      // true because `teamName` did not change, so Save is still available.
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared immediately so picking the same file twice fires a change event
    // the second time too.
    e.target.value = "";
    if (!file || !onPhotoChange || uploading) return;
    setUploading(true);
    try {
      await onPhotoChange(file);
    } catch {
      // As above — the container owns the message.
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (!onRemovePhoto || uploading) return;
    setUploading(true);
    try {
      await onRemovePhoto();
    } catch {
      // As above.
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 640 }}>
      {canManage && (
      <div className="wh-card" style={{ padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('detail.settings.teamPicture')}</div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 16 }}>
          {t('detail.settings.teamPictureHint')}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{
            width: 96, height: 96, borderRadius: 12, flexShrink: 0, overflow: "hidden",
            background: `linear-gradient(160deg, #1a3321 0%, #20502b 100%)`,
            border: `1.5px solid ${COLORS.outlineVariant}`,
          }}>
            {teamPhoto && (
              <img src={teamPhoto} alt={t('detail.settings.teamPhotoAlt')} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
            <button className="wh-btn wh-btn-outline" disabled={!onPhotoChange || uploading} onClick={() => fileInputRef.current?.click()}>
              <Icon name="upload" size={16} />{uploading ? t('detail.settings.uploading') : t('detail.settings.uploadPhoto')}
            </button>
            {teamPhoto && onRemovePhoto && (
              <button className="wh-btn wh-btn-text" style={{ color: COLORS.error }} disabled={uploading} onClick={removePhoto}>
                {t('detail.settings.removePhoto')}
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {isOwner && (
        <div className="wh-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('detail.settings.teamName')}</div>
          <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 16 }}>
            {t('detail.settings.teamNameHint')}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={{
                flex: 1, height: 40, padding: "0 12px", borderRadius: 8,
                border: `1.5px solid ${COLORS.outlineVariant}`, background: COLORS.surface,
                fontSize: 14, color: COLORS.onSurface, outline: "none",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = COLORS.primary)}
              onBlur={(e) => (e.currentTarget.style.borderColor = COLORS.outlineVariant)}
            />
            <button
              className="wh-btn wh-btn-primary"
              disabled={!onRename || !dirty || draft.trim() === "" || saving}
              onClick={save}
            >
              {saving ? t('detail.settings.saving') : t('detail.common.save')}
            </button>
          </div>
        </div>
      )}

      {/* Leaving is irreversible for the user, so it sits here behind the tab
          rather than on the page header, where it was one stray click from the
          team's name. Every role but the Owner gets it — even a plain member,
          for whom this card is the whole tab. */}
      {onLeaveTeam && (
        <div className="wh-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{t('detail.settings.dangerZone')}</div>
          <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 16 }}>
            {t('detail.settings.leaveHint')}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="wh-btn wh-btn-danger" onClick={() => setConfirmingLeave(true)}>
              <Icon name="logout" size={16} />
              {t('detail.settings.leaveTeam')}
            </button>
          </div>
        </div>
      )}

      {/* Owner-only. The Owner is offered delete rather than leave: leaving
          would strand the team without an owner, so they transfer ownership
          first, which is what the hint explains. */}
      {isOwner && (
        <div className="wh-card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{t('detail.settings.dangerZone')}</div>
          <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 16 }}>
            {t('detail.settings.deleteHint')}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ fontSize: 13, color: COLORS.outline, display: "inline-flex", alignItems: "center", gap: 6, flex: 1 }}>
              <Icon name="info" size={14} />
              {t('detail.settings.transferOwnershipHint')}
            </div>
            <button className="wh-btn wh-btn-danger" onClick={() => setConfirmingDelete(true)}>
              <Icon name="trash" size={16} />
              {t('detail.settings.deleteTeam')}
            </button>
          </div>
        </div>
      )}

      {confirmingDelete && (
        <DeleteTeamModal
          teamName={teamName}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={async () => {
            await onDeleteTeam?.();
            setConfirmingDelete(false);
          }}
        />
      )}

      {confirmingLeave && (
        <ConfirmModal
          title={t('detail.settings.leaveTeam')}
          // Both halves are complete sentences in every locale, so joining them
          // is safe: the question alone never said what leaving actually costs.
          body={`${t('detail.settings.leaveConfirm', { name: teamName })} ${t('detail.settings.leaveHint')}`}
          onCancel={() => setConfirmingLeave(false)}
          onConfirm={() => {
            setConfirmingLeave(false);
            onLeaveTeam?.();
          }}
        />
      )}
    </div>
  );
}

// Type-to-confirm delete of the whole team: it cannot be undone and removes the
// team's map, hunts, documents and pictures for every member, so the owner must
// type the team name before the destructive action is enabled. The mobile app
// asks the same way and shares the matching rule (`teamNameMatches`).
//
// The modal stays open while the delete runs and after it fails, so the owner
// sees the outcome where they acted and can retry without retyping the name. On
// success the caller navigates away, which unmounts it.
function DeleteTeamModal({
  teamName,
  onCancel,
  onConfirm,
}: {
  teamName: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const { t } = useTranslation('teams');
  const [typed, setTyped] = useState("");
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const canDelete = teamNameMatches(typed, teamName) && !pending;

  // A delete already sent cannot be called back, so nothing dismisses the modal
  // while it runs.
  const cancel = () => { if (!pending) onCancel(); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") cancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const confirm = async () => {
    if (!canDelete) return;
    setPending(true);
    setFailed(false);
    try {
      await onConfirm();
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <ModalShell onCancel={cancel} maxWidth={460}>
      <div style={{ padding: "22px 24px 6px" }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 22, fontWeight: 500, marginBottom: 8 }}>
          {t('detail.deleteModal.title')}
        </div>
        <div style={{ fontSize: 13, color: COLORS.outline, lineHeight: 1.55, marginBottom: 16 }}>
          {t('detail.deleteModal.bodyPrefix')} <strong>{teamName}</strong> {t('detail.deleteModal.bodySuffix')}
        </div>
        <label style={{ fontSize: 13, color: COLORS.onSurface, display: "block", marginBottom: 8 }}>
          {t('detail.deleteModal.typePrefix')} <strong>{teamName}</strong> {t('detail.deleteModal.typeSuffix')}
        </label>
        <input
          autoFocus
          value={typed}
          disabled={pending}
          onChange={(e) => setTyped(e.target.value)}
          aria-label={t('detail.deleteModal.confirmNameLabel')}
          placeholder={teamName}
          autoComplete="off"
          spellCheck={false}
          style={{
            width: "100%", height: 40, padding: "0 12px", borderRadius: 8,
            border: `1.5px solid ${COLORS.outlineVariant}`, background: COLORS.surface,
            fontSize: 14, color: COLORS.onSurface, outline: "none", boxSizing: "border-box",
          }}
          onKeyDown={(e) => { if (e.key === "Enter") void confirm(); }}
        />
        {failed && (
          <div role="alert" style={{ fontSize: 13, color: COLORS.error, marginTop: 10 }}>
            {t('detail.deleteModal.error')}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          padding: "16px 24px",
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
        }}
      >
        <button className="wh-btn wh-btn-outline" onClick={cancel} disabled={pending}>
          {t('detail.common.cancel')}
        </button>
        <button
          className="wh-btn wh-btn-danger"
          onClick={() => void confirm()}
          disabled={!canDelete}
          aria-busy={pending}
        >
          <Icon name="trash" size={14} />
          {pending ? t('detail.deleteModal.deleting') : t('detail.deleteModal.confirmButton')}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────

function EmptyState({ title, subtitle, icon }: { title: string; subtitle: string; icon: IconName }) {
  return (
    <div className="wh-card" style={{ padding: 48, textAlign: "center" }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 999,
          margin: "0 auto 16px",
          background: COLORS.primaryContainer,
          color: COLORS.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name={icon} size={28} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 14, color: COLORS.outline }}>{subtitle}</div>
    </div>
  );
}

function Toast({ message }: { message: string }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: COLORS.primary,
        color: COLORS.onPrimary,
        padding: "12px 18px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        zIndex: 1500,
      }}
    >
      <Icon name="check" size={16} />
      {message}
    </div>
  );
}
